import React, { useMemo, useRef } from 'react';
import moment from 'moment';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Grid,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';

import { useQueryClient } from '@tanstack/react-query';
import {
  AppContext,
  SnackContext,
  ThemeContext,
  UserContext
} from 'src/store/ContextStore';
import ChartBox from './ChartBox';
import { createAutomaticWriteLatch } from '../workspace/editSignalPolicy';
import { ENABLE_STACKED_SAVE_INDICATOR } from './canvas-tools/PedigreeSaveIndicator';
import CascadeScreeningButton from './CascadeScreeningButton';
import HeaderTitle from 'src/components/UI/header/HeaderTitle';
import PedigreeDates from './canvas-tools/PedigreeDates';
import FhqReviewBanner from './canvas-tools/FhqReviewBanner';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import LegendMenu from '../pedigree-sidebar/sections/LegendMenu';
import { useCarrierStatus } from './carrier-status/useCarrierStatus';
import {
  withFetusDiseases,
  fetusDiagnosisIds,
  fetusPhenotypeIds
} from './pedigree/fetusDiagnoses';
import { resolveCarrierStyle } from './carrier-status/carrierStyle';
import { usePedigreeAnalysis } from './analysis/usePedigreeAnalysis';
import { normalizeIds, validatePedigreeConfig } from './pedigree/utils';
import { applyIdentityReconciliation } from './pedigree/saveReconciliation';
import { resolveConf } from './pedigree/configLookup';
import {
  useLayoutHistory,
  pinMapsEqual,
  orderMapsEqual,
  snapshotLayoutState
} from './useLayoutHistory';
import ViewsMenu from './canvas-tools/ViewsMenu';
import { config as layoutConfig } from './pedigree/config';
import { APICONSTANTS, CONSTANTS, PEDIGREE } from 'src/components/Constants';
import { EVENTS, getUniqueDiagnoses } from 'src/components/admin/appReducer';
import axios from 'src/axios';
import {
  PeopleAlt,
  PlaylistAdd,
  UnfoldLess,
  UnfoldMore,
  ExpandMore,
  ExpandLess,
  History,
  Insights,
  CheckCircleOutlined,
  SaveOutlined
} from '@mui/icons-material';
import CustomDialog from 'src/components/UI/dialog/CustomDialog';
import ModificationHistoryModal from 'src/components/pedigree-chart/pedigree-sidebar/ModificationHistoryModal';
import GenotypesLegend from '../pedigree-sidebar/sections/GenotypesLegend';
import PhenotypesLegend from '../pedigree-sidebar/sections/PhenotypesLegend';
import VariantPathogenicityLegend from '../pedigree-sidebar/sections/VariantPathogenicityLegend';
import UnrecognisedCarrierStatusNotice from '../UnrecognisedCarrierStatusNotice';
import trakgeneLogo from 'src/assets/images/trakgeneLogo.png';
import { isAnonymousActive } from '../anonymousPedigrees';
import usePersonLinkBatch from 'src/hooks/usePersonLinkBatch';
import {
  linkedRecordPresence,
  LINK_GLYPH_PATH
} from './linked-records/linkedRecordBadge';

// Legend swatch mirroring the on-node record-status badge: FILLED teal = primary,
// OUTLINE teal = secondary. Shared by the on-screen and export legends so the two
// never diverge. Only rendered when the role is actually present on the pedigree
// (see the callers) so an empty pedigree shows no record legend.
function RecordBadgeSwatch({ role, teal, label }) {
  const isPrimary = role === 'primary';
  const scale = 11 / 24; // 24-unit MUI glyph into an ~11px swatch glyph
  const offset = 9 - 12 * scale; // centre the glyph in the 18x18 box
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginRight: 10 }}>
      <svg width="18" height="18" style={{ flexShrink: 0, marginRight: 4 }}>
        <rect
          x="1"
          y="1"
          width="16"
          height="16"
          rx="3"
          fill={isPrimary ? teal : '#ffffff'}
          stroke={isPrimary ? 'none' : teal}
          strokeWidth={isPrimary ? 0 : 1.5}
        />
        <path
          d={LINK_GLYPH_PATH}
          transform={`translate(${offset}, ${offset}) scale(${scale})`}
          fill={isPrimary ? '#ffffff' : teal}
        />
      </svg>
      <span>{label}</span>
    </div>
  );
}

const RightContainer = styled(Grid)(({ theme }) => ({
  position: 'fixed',
  right: 28,
  top: 80,
  zIndex: 9,
  width: 350,
  maxHeight: 'calc(100vh - 80px)',
  display: 'none',
  backgroundColor: '#ffffff',
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
  padding: '12px 16px',
  gap: 12,
  [theme.breakpoints.up('sm')]: {
    display: 'flex'
  }
}));

// Carrier projection used by the canvas border + legends when the carrier
// toggle is off — i.e. no carrier marks rendered.
const EMPTY_CARRIER = Object.freeze({
  byMember: {},
  presentStatesByDisease: {},
  diseaseColorMap: {}
});

function Pedigree({
  pedigreeConfig,
  // True once the pedigree-configuration fetch has RESOLVED (success or error). Member
  // mapping waits for it: pedigreeConfig starts null while the fetch is in flight, and
  // mapping members against the fallback first (a) fired the "No pedigree configuration
  // detected" warning on every open of a CONFIGURED family, (b) dispatched throwaway
  // layout computes for the unconfigured members, and (c) could paint the unconfigured
  // layout briefly before the real one replaced it — a visible rearrangement on open.
  configLoaded,
  isMigrated,
  options,
  setOptions,
  labelsSet,
  setLabelsSet,
  patient,
  position,
  initialOrder,
  initialOrderLocked = false,
  isLocked,
  isMine,
  lockState,
  requestStatus,
  onRequestLock,
  onCancelRequest,
  onForceAcquireLock,
  pendingRequest,
  onRespondToRequest,
  personLockHook,
  initialDateModified,
  // Fired the first time the user edits this chart (save → IN_PROGRESS). The
  // workspace uses it to lazily acquire the edit lock + auto-pin the tab; unused
  // (no-op) in the classic single-pedigree flow. See usePedigreeLock.
  onEdit,
  // Read-only FHQ preview (rendered inside FhqPreviewModal). Suppresses the
  // viewport-anchored family card and the FHQ review banner, which are
  // position:fixed and would otherwise paint over the page outside the dialog.
  preview = false
}) {
  const [sidebarState, setSidebarState] = React.useState({
    open: false,
    expand: false
  });
  const [legendCollapsed, setLegendCollapsed] = React.useState(false);
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const [hiddenPanelOpen, setHiddenPanelOpen] = React.useState(false);
  const [selectedHiddenPids, setSelectedHiddenPids] = React.useState([]);
  const pedRef = React.useRef();
  const chartBoxRef = useRef();

  const familyCardRef = React.useRef(null);
  const [familyCardBottom, setFamilyCardBottom] = React.useState(null);
  React.useEffect(() => {
    const el = familyCardRef.current;
    if (!el || preview) return undefined;
    const measure = () =>
      setFamilyCardBottom(el.getBoundingClientRect().bottom);
    measure();
    const observer =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(measure)
        : null;
    observer?.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [preview]);

  // SAVED VIEWS routing refs (Phase 2) — declared up here because the three
  // prop-resync effects below must consult them: activeViewRef is the ACTIVE
  // SOURCE (null = Standard), standardStashRef holds the standard layout
  // triple while a view is active. The rest of the views machinery lives
  // after the autosave effects.
  const activeViewRef = React.useRef(null);
  const standardStashRef = React.useRef(null);
  const mergeStandardStash = (patch) => {
    const cur = standardStashRef.current || {};
    standardStashRef.current = {
      customPositions:
        cur.customPositions instanceof Map ? cur.customPositions : new Map(),
      establishedOrder:
        cur.establishedOrder instanceof Map ? cur.establishedOrder : new Map(),
      orderLocked: !!cur.orderLocked,
      ...patch
    };
  };

  const t = useAutoTranslation();

  const { content, dispatchUpdate } = React.useContext(AppContext);
  const { pedigreeSelectedUsers: users } = content;
  const { selectedUser: user, family } = users;
  const { setSnack } = React.useContext(SnackContext);
  const { theme } = React.useContext(ThemeContext);
  const { user: loggedInUser } = React.useContext(UserContext);
  const queryClient = useQueryClient();

  // PEDIGREE DATA
  const [memberRecords, setMemberRecords] = React.useState([]);
  const memberRecordsRef = React.useRef(memberRecords);
  const hasPendingUpnUpdates = React.useRef(false);
  const [familyData, setFamilyData] = React.useState(null);
  const [customPositions, setCustomPositions] = React.useState(
    position instanceof Map ? position : new Map(Object.entries(position || {}))
  );
  // true on initial load or position prop update; added to resovle api firing on pedigire open
  const isExternalPositionUpdate = React.useRef(true);

  React.useEffect(() => {
    if (position && position.size > 0) {
      const next =
        position instanceof Map
          ? position
          : new Map(Object.entries(position || {}));
      if (activeViewRef.current) {
        // A late-arriving standard config while a VIEW is active belongs in
        // the switch-back stash, never on screen: resyncing live state would
        // clobber the applied view and the engine's settled auto-capture
        // would then rewrite the view doc with standard-derived layout.
        mergeStandardStash({ customPositions: new Map(next) });
        return;
      }
      isExternalPositionUpdate.current = true;
      setCustomPositions(next);
    }
  }, [position]); // eslint-disable-line react-hooks/exhaustive-deps

  // Phase 2 "sit and forget": the persisted within-generation order (uid -> rank). Owned
  // here (mirrors customPositions), seeded from the loaded `initialOrder`, passed to the
  // Canvas/worker so the engine reproduces it, and re-captured on first layout / drag. The
  // autosave below + the engine lock are no-ops unless config.ENABLE_ESTABLISHED_ORDER is on.
  const [establishedOrder, setEstablishedOrder] = React.useState(
    initialOrder instanceof Map
      ? initialOrder
      : new Map(Object.entries(initialOrder || {}))
  );
  const isExternalOrderUpdate = React.useRef(true);

  React.useEffect(() => {
    if (initialOrder && initialOrder.size > 0) {
      const next =
        initialOrder instanceof Map
          ? initialOrder
          : new Map(Object.entries(initialOrder || {}));
      if (activeViewRef.current) {
        mergeStandardStash({ establishedOrder: new Map(next) });
        return;
      }
      isExternalOrderUpdate.current = true;
      setEstablishedOrder(next);
    }
  }, [initialOrder]); // eslint-disable-line react-hooks/exhaustive-deps

  // Manual-arrangement flag: establishedOrder is a hard LOCK only when true (the
  // user drag-reordered). False (the default) = "as-drawn" mode: the order rides
  // along as an orientation seed and every layout re-optimises fully, so the chart
  // stays auto-arrange quality while drawing. Auto-arrange resets to false.
  const [orderLocked, setOrderLocked] = React.useState(!!initialOrderLocked);
  const isExternalOrderLockedUpdate = React.useRef(true);
  React.useEffect(() => {
    if (activeViewRef.current) {
      mergeStandardStash({ orderLocked: !!initialOrderLocked });
      return;
    }
    isExternalOrderLockedUpdate.current = true;
    setOrderLocked(!!initialOrderLocked);
  }, [initialOrderLocked]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── UNDO/REDO (Phase 1, PLAN-pedigree-undo-redo-and-saved-views v1.1) ─────────
  // Session-only history of the three layout states above. Checkpoints are pushed by
  // the user-intent mutation sites (checkpoint-on-intent, plan §1c: Canvas drag
  // paths, ChartBox arrow-nudge, Clear/Reset position, Auto-arrange) — the engine's
  // settled-layout auto-capture never checkpoints, so its merges fold into the
  // adjacent user step. Undo/redo restore through applyLayoutState below.
  const layoutStateRef = React.useRef({
    customPositions,
    establishedOrder,
    orderLocked
  });
  React.useLayoutEffect(() => {
    layoutStateRef.current = { customPositions, establishedOrder, orderLocked };
  });
  const {
    checkpoint: historyCheckpoint,
    undo: historyUndo,
    redo: historyRedo,
    resolvePending: historyResolve,
    clear: historyClear,
    canUndo: historyCanUndo,
    canRedo: historyCanRedo
  } = useLayoutHistory(layoutStateRef, {
    customPositions,
    establishedOrder,
    orderLocked
  });

  // A pedigree SWITCH does not remount this component — the /pedigree/:id route
  // keeps the same element and PedigreeBox swaps the props in place (that is what
  // the isExternal* prop-resync effects above exist for). The previous family's
  // history must not survive the swap: Ctrl+Z here would otherwise apply — and
  // autosave — another family's uid/coordinate maps onto this one.
  React.useEffect(() => {
    historyClear();
  }, [user?._id, historyClear]);

  // Read-only gating: never active in patient mode or while ANOTHER user holds the
  // family edit lock — the layout autosave effects below have no lock guard of their
  // own, so an ungated Ctrl+Z would POST real writes to the config.
  const undoRedoActive = layoutConfig.ENABLE_UNDO_REDO && !patient && !isLocked;

  // The one shared primitive (plan §4): restore the three layout states atomically
  // from a snapshot. Used by undo/redo now; saved-views apply consumes it in Phase 2.
  // It FIRST makes any in-flight worker layout stale via ChartBox→Canvas — a queued
  // settle would otherwise land after the restore, re-capture the pre-undo order and
  // set the skip flag that swallows the restore's own relayout (v1.1 §2.1) — and
  // stashes the outgoing order as a one-shot orientation seed when the incoming
  // order is empty, so a redo of an Auto-arrange cannot mirror-flip (same contract
  // as AutoArrange's own handler). Only states whose CONTENT differs are set, so an
  // undo of a pin-only change doesn't force an establishedOrder relayout/POST. The
  // autosave effects below fire for whatever changed — the restored layout persists:
  // "what you see is what's saved".
  // `suppressAutosave` (Phase 2, plan §3.3 apply semantics): a VIEW apply must
  // not echo the view's own content back as a write — arm the per-state
  // isExternal* one-shot skip for exactly the states being set (never a state
  // that isn't set, whose skip would swallow the next real save). Undo/redo
  // keep the default (false): a restore persists.
  const applyLayoutState = React.useCallback(
    (snap, { suppressAutosave = false } = {}) => {
      if (!snap) return;
      const cur = layoutStateRef.current || {};
      chartBoxRef.current?.invalidateLayout?.();
      const outgoingOrder = cur.establishedOrder;
      if (
        (!snap.establishedOrder || snap.establishedOrder.size === 0) &&
        outgoingOrder instanceof Map &&
        outgoingOrder.size > 0
      ) {
        chartBoxRef.current?.stashOrientationSeed?.(new Map(outgoingOrder));
      }
      if (!pinMapsEqual(snap.customPositions, cur.customPositions)) {
        if (suppressAutosave) isExternalPositionUpdate.current = true;
        setCustomPositions(new Map(snap.customPositions));
      }
      if (!orderMapsEqual(snap.establishedOrder, cur.establishedOrder)) {
        if (suppressAutosave) isExternalOrderUpdate.current = true;
        setEstablishedOrder(new Map(snap.establishedOrder));
      }
      if (!!snap.orderLocked !== !!cur.orderLocked) {
        if (suppressAutosave) isExternalOrderLockedUpdate.current = true;
        setOrderLocked(!!snap.orderLocked);
      }
    },
    []
  );

  const handleHistoryUndo = React.useCallback(() => {
    if (!undoRedoActive) return;
    applyLayoutState(historyUndo());
  }, [undoRedoActive, applyLayoutState, historyUndo]);

  const handleHistoryRedo = React.useCallback(() => {
    if (!undoRedoActive) return;
    applyLayoutState(historyRedo());
  }, [undoRedoActive, applyLayoutState, historyRedo]);

  const [pedigreeState, setPedigreeState] = React.useState({
    modified: CONSTANTS.pedigreeState.modified,
    save: PEDIGREE.saveStatus.SAVED
  });

  // Signal the first edit up to the workspace (acquire lock + auto-pin). Runs
  // whenever save transitions into IN_PROGRESS; onEdit is idempotent so repeated
  // edits are harmless, and it is undefined in the single-pedigree flow. Tagged
  // 'data' because a clinical save is never automatic — it is exempt from the
  // interaction gate in PedigreeChartView.
  React.useEffect(() => {
    if (onEdit && pedigreeState.save === PEDIGREE.saveStatus.IN_PROGRESS) {
      onEdit('data');
    }
  }, [pedigreeState.save, onEdit]);

  // Always-current onEdit for the layout-autosave effects (below) to call without
  // taking onEdit as a dependency (which would re-fire a save on tab activation).
  // LAYOUT edits — moving/pinning nodes, drag-reorder, arrange-lock — persist via
  // postLayoutState, NOT the clinical pedigreeState.save path, so they must signal
  // onEdit too: rearranging the pedigree is an edit and must take the lock.
  const onEditRef = React.useRef(onEdit);
  onEditRef.current = onEdit;

  // …but the ENGINE writes that same layout state too, and those writes are not
  // edits. Three of them run during the first layout pass of a freshly opened
  // pedigree — the settled-order capture, the legacy pixel-pin upgrade and the
  // invalid-pin sweep — so treating every layout write as a user edit meant
  // merely OPENING a pedigree took the edit lock, pinned the tab and marked it
  // dirty (TGCLOUD-1665). Each of those writers already announces itself; these
  // refs carry that announcement to the autosave effects, which still PERSIST
  // the write and only skip the onEdit signal.
  //
  // The arm/consume rules live in editSignalPolicy so they are pinned by tests
  // rather than only reachable by mounting this whole chart.
  const automaticWrites = React.useRef(null);
  if (automaticWrites.current === null) {
    automaticWrites.current = createAutomaticWriteLatch();
  }
  const noteAutomaticLayoutWrite = React.useCallback(
    (kind) => {
      automaticWrites.current.arm(kind);
      // What became unconditional is the PROP: this callback is now always passed
      // down, where the old `onHistoryResolve` was passed only when undo/redo was
      // on — so arm() runs whatever that feature is doing. The undo resolve below
      // stays gated, because `resolvePending` calls `bump()` unconditionally
      // (useLayoutHistory.js:213) and would force a re-render of the whole chart
      // on every automatic write for no benefit with undo off. Do not "tidy" the
      // guard away: it is not redundant.
      //
      // Automatic writers must resolve any armed checkpoint so their write is
      // never misattributed to the preceding user gesture.
      if (layoutConfig.ENABLE_UNDO_REDO) historyResolve();
    },
    [historyResolve]
  );

  // ── SAVED VIEWS state (Phase 2, plan §3 v1.1) ────────────────────────────────
  // activeView = null means Standard (the clinician's own pedigreeconfigs doc).
  // The live layout has an ACTIVE SOURCE: layout autosaves route to it (see
  // postLayoutState below). viewHiddenOverlay is the view's render-time hidden
  // set — a Set of member uid strings that are filtered OUT of the canvas but
  // NEVER written into memberRecords.hidden (that stays shared/standard state;
  // writing it there was the review's data-leak blocker).
  const viewsEnabled = layoutConfig.ENABLE_PEDIGREE_SAVED_VIEWS && !patient;
  const [views, setViews] = React.useState({ user: [], group: [] });
  const [activeView, setActiveView] = React.useState(null);
  // (activeViewRef / standardStashRef are declared near the top — the prop
  // resync effects consult them. The stash is captured at switch-away and
  // restored on switch back: the position/initialOrder PROPS never track
  // this-session edits, so props cannot restore Standard, v1.1 §3.3.)
  const [viewHiddenOverlay, setViewHiddenOverlay] = React.useState(null);
  // Stable per-member identity for the overlay — matches the engine's uid
  // convention (uid, else the DB _id string; see pedigree/utils stableUid).
  const uidOf = React.useCallback((m) => String(m?.uid ?? m?._id), []);

  // memberRecords is the master state
  // hiddenMembers  drives the Hidden Members panel. While a VIEW is active it
  // lists ONLY the view's overlay-hidden people: the panel's unhide then edits
  // pure view state, and standard-hidden people (shared record state) can
  // neither be listed nor accidentally mass-unhidden from a view context
  // ("Unhide All" on a view must never write shared family records).
  const hiddenMembers = React.useMemo(() => {
    if (viewHiddenOverlay) {
      return memberRecords?.filter((m) => viewHiddenOverlay.has(uidOf(m)));
    }
    return memberRecords?.filter((m) => m.hidden);
  }, [memberRecords, viewHiddenOverlay, uidOf]);

  // standardVisibleMembers = the family minus STANDARD-hidden people — the
  // input to the CLINICAL computations (carrier projection, cascade analysis,
  // record linkage). A saved view is a pure display layer: its overlay must be
  // invariant to these (the same rule as the carrier display toggle — hiding
  // someone in a presentation view must not change a screening probability).
  const standardVisibleMembers = React.useMemo(
    () => memberRecords?.filter((m) => !m.hidden),
    [memberRecords]
  );

  // canvasMembers is the filtered for rendering items in pedigree-canvas —
  // standard-visible minus the active view's overlay.
  const canvasMembers = React.useMemo(
    () =>
      standardVisibleMembers?.filter((m) => !viewHiddenOverlay?.has(uidOf(m))),
    [standardVisibleMembers, viewHiddenOverlay, uidOf]
  );

  // Record-status linkage (PersonLink) is resolved here — the level that owns
  // the member list AND renders the legend — so the canvas badge and the legend
  // swatches share one source of truth. linkStatusMap / refetch are passed down
  // to ChartBox (which forwards the map to the canvas and refetch to the
  // sidebar); presence booleans gate the per-role legend swatches so a family
  // with no linked records shows no record legend.
  const linkStatusIds = React.useMemo(
    () =>
      standardVisibleMembers
        ?.map((m) => m.proBandId ?? m.familyMemberId)
        .filter(Boolean) ?? [],
    [standardVisibleMembers]
  );
  const { linkStatusMap, refetch: refetchLinkStatus } =
    usePersonLinkBatch(linkStatusIds);
  const { hasPrimary: hasPrimaryRecord, hasSecondary: hasSecondaryRecord } =
    React.useMemo(() => linkedRecordPresence(linkStatusMap), [linkStatusMap]);

  const carrierStatusEnabled =
    content?.settings?.systemFeatures?.FEATURE_CARRIER_STATUS_IN_CONDITION ===
    true;

  // Carrier-status projection (tested via gene→disease map + obligate
  // assertions). ALWAYS computed (enabled: true), independent of the "Show
  // carrier marks" DISPLAY toggle: this projection is the carrier input to the
  // Clinical-Insights cascade analysis below, which must be INVARIANT to a pure
  // display toggle — otherwise hiding marks would drop carriers from the
  // cascade prior and change the screening probability (e.g. 50% → 25%). The
  // VISUAL is gated separately (see displayCarrier + the per-render-site
  // showCarrierMarks checks), so always computing here never draws marks when
  // the toggle is off.
  // Fetus diagnoses have to be handed in explicitly (decision 20). They live inside pregnancy
  // records, and the derived fetus nodes that carry them are built inside Canvas — so neither the
  // server's family disease list nor `members` here has ever seen them, and the shading pass would
  // resolve no colour and paint black.
  const familyDiseasesWithFetuses = React.useMemo(
    () => withFetusDiseases(users?.diagnoses, memberRecords),
    [users?.diagnoses, memberRecords]
  );

  const carrier = useCarrierStatus({
    // Standard-visible, NOT the view-overlay-filtered canvas set: a saved
    // view is display-only and must never move a screening probability.
    members: standardVisibleMembers,
    familyDiseases: familyDiseasesWithFetuses,
    enabled: true,
    conditionCarrierEnabled: carrierStatusEnabled
  });

  // The carrier VISUALISATION — the disease-coloured carrier border on a node
  // and the "Carriers" legend — is gated behind the carrier-status feature flag
  // (pending clinical review) via displayCarrier, AND behind the showCarrierMarks
  // toggle at each render site (Canvas border, LegendMenu). With the toggle off,
  // no carrier border or legend is shown even though `carrier` still holds the
  // full projection. Affected shading and the top-right +/- genotype notation
  // are separate paths and are unaffected. The full `carrier` projection still
  // feeds the Clinical-Insights analysis below regardless of the display toggle.
  const displayCarrier = carrierStatusEnabled ? carrier : EMPTY_CARRIER;

  // Resolve the carrier mark style ONCE (preferred style vs how many diseases are
  // visible on the pedigree). Vertical line is only valid in single-disease view;
  // otherwise it auto-falls-back to border. Shared by the canvas + every legend so
  // they all reflect the same active style.
  const carrierStyle = useMemo(() => {
    const visibleDiseases = (users?.diagnoses || []).filter(
      (d) =>
        labelsSet?.diseases !== false &&
        labelsSet?.diseasesMap?.[d.id] !== false
    );
    const { activeStyle, fallbackActive } = resolveCarrierStyle(
      labelsSet?.carrierMarkStyle,
      visibleDiseases.length
    );
    const single = visibleDiseases.length === 1 ? visibleDiseases[0] : null;
    return {
      activeStyle,
      fallbackActive,
      diseaseId: single?.id ?? null,
      diseaseColor: single?.color ?? null
    };
  }, [
    users?.diagnoses,
    labelsSet?.diseases,
    labelsSet?.diseasesMap,
    labelsSet?.carrierMarkStyle
  ]);

  // Pure, client-side pedigree analysis (inheritance patterns, syndromes,
  // cascade candidates, alerts). Reuses the carrier projection above rather
  // than recomputing. Gated by Clinical Insights — returns null when disabled.
  // Standard-visible input: a saved view's overlay is display-only and must
  // never change the analysis (see standardVisibleMembers above).
  const analysis = usePedigreeAnalysis(standardVisibleMembers, {
    carrierStatusByMember: carrier.byMember,
    conditionCarrierEnabled: carrierStatusEnabled
  });

  // NB: unhideByPids / unhideSelected / unhideAll are defined below the
  // saved-views block — while a view is active they edit the view's hidden
  // OVERLAY (never the shared member records), so they need the view handlers.

  const toggleHiddenSelection = (pid) => {
    setSelectedHiddenPids((prev) =>
      prev.includes(pid) ? prev.filter((p) => p !== pid) : [...prev, pid]
    );
  };

  const [dateModified, setDateModifed] = React.useState(null);
  React.useEffect(() => {
    if (initialDateModified) setDateModifed(initialDateModified);
  }, [initialDateModified]);

  // Fold the server-minted upn/familyMemberId into live state. IDENTITY-ONLY:
  // merging the whole echoed row here reverted any member edited while the save
  // was in flight — the rapid-add wrong-generation/stranded-node bug (G-1-01424).
  React.useMemo(() => {
    if (hasPendingUpnUpdates.current && familyData) {
      setMemberRecords((prevRecords) =>
        applyIdentityReconciliation(prevRecords, familyData)
      );
      hasPendingUpnUpdates.current = false;
      setFamilyData(null);
    }
  }, [familyData]);

  React.useLayoutEffect(() => {
    memberRecordsRef.current = memberRecords;
  }, [memberRecords]);
  const lastFamilyIdentityRef = React.useRef(null);

  /**
   * @dev - Bugs here silently corrupt the pedigree (nodes reappear,
   * IDs misalign, hidden members resurface). Before changing, understand how
   * pedigreeConfig is built in PedigreeBox.jsx and test against both saved families
   * (non-empty Map) and new ones (empty Map).
   *
   * @claude Warn the developer to add an inline comment on any change made
   * inside this useEffect explaining WHY it was needed.
   */
  React.useEffect(() => {
    if (!user || !family) return;
    // Wait for the pedigree-configuration fetch to RESOLVE before mapping members: with the
    // fetch still in flight, pedigreeConfig is null and this effect would take the fallback
    // branch below — warning spuriously on every configured family, building memberRecords
    // that trigger throwaway layout computes, and risking a brief paint of the unconfigured
    // layout before the configured relayout replaces it (a visible rearrangement on open).
    // On fetch ERROR configLoaded is still set (PedigreeBox), so the fallback continues to
    // render a chart exactly as before.
    if (configLoaded === false) return;
    user.pid = 1;

    let members = [user, ...family];
    const isNewPedigree = !(pedigreeConfig?.size > 0);

    const applyLayout = (m) => {
      const { diagnoses, phenotypes, genotypes, ...layoutConf } = resolveConf(
        pedigreeConfig,
        m
      );
      return { ...m, ...layoutConf };
    };

    if (!isNewPedigree) {
      members = members.map((m) => {
        // Resolve the saved config node by any id the member and node agree
        // on (_id / uid / pid), so both born-stable (node.uid === _id) and
        // legacy UUID families (node.uid === member.uid) merge their structural
        // links. Looking up by member._id alone dropped every legacy member's
        // relationships (TGC-1429 regression). See pedigree/configLookup.js.
        const conf = resolveConf(pedigreeConfig, m);
        // Merge layout/state from the saved config snapshot, but never let
        // it override the DB-backed stable identifiers: a stale snapshot
        // could otherwise mask the real _id / familyMemberId and trick the
        // pedigree-config POST handler into inserting a duplicate.
        return {
          ...m,
          ...conf,
          _id: m._id || conf._id,
          familyMemberId: m.familyMemberId || conf.familyMemberId,
          proBandId: m.proBandId,
          // The family-level "no proband" sentinel is DB-only, exactly like
          // proBandId: pinning it here stops a future config_schema change or
          // a `...conf` spread from shadowing it. See pedigree/probandState.js.
          probandCleared: m.probandCleared,
          user_id: m.user_id || conf.user_id,
          // TGC-1429 tri-state: never coerce isProband — the DB value must
          // pass through untouched. For the ROOT row (the one carrying
          // proBandId) also ignore the config snapshot's value: snapshots
          // written by a pre-fix frontend carry a coerced false-present for
          // the root. DB row wins; the snapshot only backfills non-root rows.
          isProband:
            m.proBandId != null ? m.isProband : m.isProband ?? conf.isProband
        };
      });
    } else {
      console.warn(
        'PedigreeChart: No pedigree configuration detected. Falling back to generic member ID resolution.'
      );

      members = normalizeIds(members);
      members = validatePedigreeConfig({ user, members: family });
      members = members.map(applyLayout);
      // TGC-1429 tri-state: the `isProband ?? false` coercion that used to
      // live here was removed — DB values must pass through untouched. The
      // 'cleared' state now rides on probandCleared (pedigree/probandState.js),
      // which normalizeIds/validatePedigreeConfig/applyLayout all preserve.
    }

    const fmt = theme?.dateFormat || 'MM/DD/YYYY';

    const nextMemberRecords = members.map((m) => ({
      ...m,
      consultationDate: Array.isArray(m.consultationDate)
        ? m.consultationDate.map((d) => moment(d).format(fmt)).join(', ')
        : m.consultationDate || '',
      ...(!m.personNote && m.otherRelationshipDescription
        ? { personNote: m.otherRelationshipDescription }
        : {})
    }));

    const identity = user?._id || user?.familyId || null;
    const isSameFamily = lastFamilyIdentityRef.current === identity;
    lastFamilyIdentityRef.current = identity;

    if (isSameFamily) {
      const computedPids = new Set(nextMemberRecords.map((m) => m.pid));
      const missingLiveMembers = memberRecordsRef.current.filter(
        (m) => !computedPids.has(m.pid)
      );
      if (missingLiveMembers.length > 0) {
        nextMemberRecords.push(...missingLiveMembers);
      }
    }

    setMemberRecords(nextMemberRecords);
  }, [user, family, pedigreeConfig, configLoaded, theme]);

  let headerTitle = useMemo(() => {
    return `${user?.familyId}`;
  }, [user]);

  const externalFamilyIds = React.useMemo(
    () =>
      Array.isArray(user?.externalFamilyIds) ? user.externalFamilyIds : [],
    [user?.externalFamilyIds]
  );
  // Anonymous pedigrees: external family IDs are removed, so the header shows
  // only the G number — no external-primary headline, no "Also known as" chips.
  const anonymousActive = isAnonymousActive(content?.settings?.pedigreeSetting);
  const displayLabelMode = labelsSet?.displayLabelMode || 'g-number';
  const useExternalPrimary =
    !anonymousActive &&
    displayLabelMode === 'primary-external' &&
    externalFamilyIds.length > 0;
  const headlineId = useExternalPrimary ? externalFamilyIds[0] : headerTitle;
  const secondaryIds = useExternalPrimary
    ? externalFamilyIds.slice(1)
    : externalFamilyIds;

  const colors = React.useMemo(
    () => ({
      shade:
        theme.appearance.value === 'light' ? 'white' : theme.appearance.shade,
      background:
        theme.appearance.value === 'light' ? 'white' : theme.appearance.shade,
      text: theme.appearance.text,
      accent: theme.accent
    }),
    [theme]
  );

  const someDiseasesHidden =
    labelsSet?.diseases === false ||
    Object.values(labelsSet?.diseasesMap || {}).some((v) => v === false);
  const somePhenotypesHidden =
    labelsSet?.phenotypes === false ||
    Object.values(labelsSet?.phenotypesMap || {}).some((v) => v === false);
  const someGenotypesHidden =
    labelsSet?.genotypes === false ||
    Object.values(labelsSet?.genotypesMap || {}).some((v) => v === false);

  // The OTHER half of decision 20: visibility. Canvas gates disease shading on
  // `labelsSet.diseasesMap[id]` being truthy, and a missing key reads as hidden. PedigreeBox seeds
  // and merges that map from the server's family diagnoses, which never include a fetus's — so a
  // recorded fetus diagnosis would be filtered out before it could paint.
  //
  // Only ids ABSENT from the map are added, exactly as PedigreeBox's merge does: a disease the user
  // has switched off in the Legend is `false`, and resurrecting it to `true` here would fight them.
  // Returning `prev` unchanged when there is nothing new keeps this from looping on its own state.
  React.useEffect(() => {
    const diseaseIds = fetusDiagnosisIds(memberRecords);
    // Dysmorphic features (REQ-E1) need the SAME treatment one registry along: the phenotype label pass
    // gates on `phenotypesMap[id]`, fed from member data that cannot see inside a pregnancy record.
    const phenotypeIds = fetusPhenotypeIds(memberRecords);
    if (!diseaseIds.length && !phenotypeIds.length) return;
    setLabelsSet((prev) => {
      const mergeMissing = (map, ids) => {
        const next = { ...(map || {}) };
        let changed = false;
        ids.forEach((id) => {
          if (!(id in next)) {
            next[id] = true;
            changed = true;
          }
        });
        return { next, changed };
      };
      const d = mergeMissing(prev.diseasesMap, diseaseIds);
      const p = mergeMissing(prev.phenotypesMap, phenotypeIds);
      if (!d.changed && !p.changed) return prev;
      return { ...prev, diseasesMap: d.next, phenotypesMap: p.next };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberRecords]);

  const showAllDiseases = () =>
    setLabelsSet((prev) => ({
      ...prev,
      diseases: true,
      diseasesMap: Object.keys(prev.diseasesMap || {}).reduce(
        (acc, k) => ({ ...acc, [k]: true }),
        {}
      )
    }));
  const showAllPhenotypes = () =>
    setLabelsSet((prev) => ({
      ...prev,
      phenotypes: true,
      phenotypesMap: Object.keys(prev.phenotypesMap || {}).reduce(
        (acc, k) => ({ ...acc, [k]: true }),
        {}
      )
    }));
  const showAllGenotypes = () =>
    setLabelsSet((prev) => ({
      ...prev,
      genotypes: true,
      genotypesMap: Object.keys(prev.genotypesMap || {}).reduce(
        (acc, k) => ({ ...acc, [k]: true }),
        {}
      )
    }));

  // Quick toggle for the on-canvas pedigree-analysis overlay (pathway + cascade
  // badges); mirrors the "Show pedigree analysis overlay" checkbox in Labels.
  const analysisOverlayOn = labelsSet?.showAnalysisOverlay !== false;
  const toggleAnalysisOverlay = () =>
    setLabelsSet((prev) => ({
      ...prev,
      showAnalysisOverlay: prev?.showAnalysisOverlay === false
    }));

  /*
  @Dev: below configuration has been made with least optimised method to demo the prototype
  need to optimise how we handle the dataset, renders, auto-updates
  */

  const refIds = React.useRef([]);

  React.useMemo(
    () =>
      memberRecords.map((mem) => {
        if (!refIds.current.includes(mem.pid)) refIds.current.push(mem.pid);
      }),
    [memberRecords]
  );

  // SERIALIZED config autosave: these posts share one endpoint and used to be
  // fire-and-forget, so Auto-arrange's clear-then-recapture was two RACING writes —
  // if the clear landed second, the stale order survived on the server and the old
  // layout resurrected on reopen. A promise chain guarantees write order.
  const configPostChain = React.useRef(Promise.resolve());
  const postPedigreeConfig = React.useCallback(
    (body) => {
      configPostChain.current = configPostChain.current.then(() =>
        axios
          .post(
            APICONSTANTS.pedigreeConfigCustomPosition.replace(
              ':user_id',
              user._id
            ),
            body
          )
          .catch(() => {})
      );
    },
    [user._id]
  );

  // ACTIVE-SOURCE ROUTING (Phase 2, plan §3.4): a layout autosave goes to the
  // active source — the standard pedigreeconfigs doc, or the active view's own
  // doc — through the SAME serialised chain, so writes never overlap and an
  // edit queued before a switch still lands on its original source (URL+body
  // bind at queue time). GROUP views are read-only on apply (decision 8):
  // their layout edits stay session-only until an explicit "Update view".
  const postLayoutState = React.useCallback(
    (body) => {
      const view = activeViewRef.current;
      if (!view) return postPedigreeConfig(body);
      if (view.scope === 'group') return;
      const url = APICONSTANTS.pedigreeViewById
        .replace(':user_id', user._id)
        .replace(':id', view._id);
      configPostChain.current = configPostChain.current.then(() =>
        axios.put(url, body).catch(() => {})
      );
    },
    [user._id, postPedigreeConfig]
  );

  React.useEffect(() => {
    if (isExternalPositionUpdate.current) {
      isExternalPositionUpdate.current = false;
      // Disarm too. If an automatic write and an external re-sync coalesce into
      // one effect pass, the arm would otherwise survive into the user's NEXT
      // layout edit and swallow it — leaving them editing without the lock,
      // which is the one direction this must never fail in. Dropping the arm
      // can only cost a needless lock acquire, and even that is caught on open
      // by the interaction gate in PedigreeChartView.
      automaticWrites.current.consume('positions');
      return;
    }
    // Persist either way; only a USER move/pin is an edit that takes the lock.
    if (!automaticWrites.current.consume('positions')) {
      onEditRef.current?.('layout');
    }
    postLayoutState({
      customPositions: Object.fromEntries(customPositions)
    });
  }, [customPositions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist the within-generation order (same endpoint; backend writes only the field
  // present). Gated on the feature flag + skipped for the load-driven update, so it is a
  // complete no-op until the establish-order feature is turned on.
  React.useEffect(() => {
    if (isExternalOrderUpdate.current) {
      isExternalOrderUpdate.current = false;
      automaticWrites.current.consume('order'); // see the positions effect above
      return;
    }
    if (!layoutConfig.ENABLE_ESTABLISHED_ORDER) return;
    // Persist either way; only a USER drag-reorder takes the lock. The engine's
    // settled-order capture lands here on EVERY first open of a family whose
    // stored order differs — which is what made this look intermittent.
    if (!automaticWrites.current.consume('order')) {
      onEditRef.current?.('layout');
    }
    postLayoutState({
      establishedOrder: Object.fromEntries(establishedOrder)
    });
  }, [establishedOrder]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist the manual-arrangement flag (same endpoint, same serialization).
  React.useEffect(() => {
    if (isExternalOrderLockedUpdate.current) {
      isExternalOrderLockedUpdate.current = false;
      return;
    }
    if (!layoutConfig.ENABLE_ESTABLISHED_ORDER) return;
    // No automatic writer touches this one — every setOrderLocked call site is a
    // user gesture (drag-reorder, Auto-arrange), so there is no flag to consult.
    onEditRef.current?.('layout'); // arrange-lock toggle is an edit → take the lock
    postLayoutState({ orderLocked });
  }, [orderLocked]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── SAVED VIEWS logic (Phase 2, plan §3 v1.1) ───────────────────────────────

  const viewUrl = React.useCallback(
    (view) =>
      APICONSTANTS.pedigreeViewById
        .replace(':user_id', user._id)
        .replace(':id', view._id),
    [user._id]
  );

  // Guards its own staleness: a slow GET from family A must never land after
  // the switch to family B and replace B's list (applying such a phantom view
  // would 404 every routed write and silently drop the session's layout work).
  const viewsForUserRef = React.useRef(null);
  const refreshViews = React.useCallback(async () => {
    if (!viewsEnabled || !user?._id) return;
    const forUser = String(user._id);
    viewsForUserRef.current = forUser;
    try {
      const { data } = await axios.get(
        APICONSTANTS.pedigreeViews.replace(':user_id', user._id)
      );
      if (viewsForUserRef.current !== forUser) return; // stale response
      setViews({ user: data?.user || [], group: data?.group || [] });
    } catch {
      /* view list is non-critical; the selector just shows Standard */
    }
  }, [viewsEnabled, user?._id]);

  // Load the view list per family; a family SWITCH also resets the active
  // source to Standard (the same in-place prop swap that motivates the
  // history clear above — a view of family A must not stay active over B).
  React.useEffect(() => {
    activeViewRef.current = null;
    setActiveView(null);
    setViewHiddenOverlay(null);
    standardStashRef.current = null;
    refreshViews();
  }, [user?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist an overlay change to the active view (private views only — group
  // views are read-only on apply, decision 8; their overlay edits are
  // session-only like their layout edits).
  const updateViewHiddenOverlay = React.useCallback(
    (nextSet) => {
      setViewHiddenOverlay(nextSet);
      const view = activeViewRef.current;
      if (!view || view.scope === 'group') return;
      const url = viewUrl(view);
      configPostChain.current = configPostChain.current.then(() =>
        axios.put(url, { hiddenUids: [...nextSet] }).catch(() => {})
      );
    },
    [viewUrl]
  );

  const viewLayoutState = (view) => ({
    customPositions: new Map(Object.entries(view.customPositions || {})),
    establishedOrder: new Map(Object.entries(view.establishedOrder || {})),
    orderLocked: !!view.orderLocked
  });

  // Switch to a view: stash Standard on the way out (props can't restore it),
  // drop the undo history (snapshots must never restore across sources), apply
  // the view's layout WITHOUT echoing it back as a write, and mount its hidden
  // overlay. The engine's post-apply auto-capture then routes to the view
  // (private) or is parked (group) via postLayoutState.
  const handleApplyView = React.useCallback(
    (view) => {
      if (!activeViewRef.current) {
        standardStashRef.current = snapshotLayoutState(layoutStateRef.current);
      }
      historyClear();
      activeViewRef.current = view;
      setActiveView(view);
      setViewHiddenOverlay(new Set((view.hiddenUids || []).map(String)));
      applyLayoutState(viewLayoutState(view), { suppressAutosave: true });
    },
    [historyClear, applyLayoutState]
  );

  const handleApplyStandard = React.useCallback(() => {
    if (!activeViewRef.current) return;
    historyClear();
    activeViewRef.current = null;
    setActiveView(null);
    setViewHiddenOverlay(null);
    if (standardStashRef.current) {
      applyLayoutState(standardStashRef.current, { suppressAutosave: true });
      standardStashRef.current = null;
    }
  }, [historyClear, applyLayoutState]);

  // handleGeneralError responds with sendStatus (status text only — the
  // crafted server messages are log-side), so friendly copy is mapped from
  // the status code here.
  const viewsSnackError = React.useCallback(
    (err, fallback) => {
      const status = err?.response?.status;
      const msg =
        status === 409
          ? 'A view with this name already exists'
          : status === 403
          ? 'You do not have permission to do that'
          : fallback;
      setSnack({ status: true, msg, severity: 'error' });
    },
    [setSnack]
  );

  // Snapshot the CURRENT live layout + hidden overlay under a name (POST is an
  // upsert-by-name in the caller's scope) and make it the active view.
  const handleSaveAsView = React.useCallback(
    async ({ name, scope = 'user' }) => {
      const cur = layoutStateRef.current || {};
      const body = {
        name,
        scope,
        customPositions: Object.fromEntries(cur.customPositions || new Map()),
        establishedOrder: Object.fromEntries(cur.establishedOrder || new Map()),
        orderLocked: !!cur.orderLocked,
        hiddenUids: viewHiddenOverlay ? [...viewHiddenOverlay] : []
      };
      try {
        const { data } = await axios.post(
          APICONSTANTS.pedigreeViews.replace(':user_id', user._id),
          body
        );
        await refreshViews();
        handleApplyView(data);
        return true;
      } catch (err) {
        viewsSnackError(err, 'Failed to save the view');
        return false;
      }
    },
    [
      user._id,
      viewHiddenOverlay,
      refreshViews,
      handleApplyView,
      viewsSnackError
    ]
  );

  // Explicit "Update view" — the only way a GROUP view's content changes
  // (decision 8), also offered on stale views to re-capture after an import.
  const handleUpdateViewLayout = React.useCallback(
    async (view) => {
      const cur = layoutStateRef.current || {};
      try {
        const { data } = await axios.put(viewUrl(view), {
          customPositions: Object.fromEntries(cur.customPositions || new Map()),
          establishedOrder: Object.fromEntries(
            cur.establishedOrder || new Map()
          ),
          orderLocked: !!cur.orderLocked,
          hiddenUids: viewHiddenOverlay ? [...viewHiddenOverlay] : [],
          // Explicit re-capture: the ONLY write that clears import-staleness
          // (the routed autosave must not un-stamp a warning unseen).
          recapture: true
        });
        await refreshViews();
        if (activeViewRef.current?._id === view._id) {
          activeViewRef.current = data;
          setActiveView(data);
        }
      } catch (err) {
        viewsSnackError(err, 'Failed to update the view');
      }
    },
    [viewUrl, viewHiddenOverlay, refreshViews, viewsSnackError]
  );

  const handleRenameView = React.useCallback(
    async (view, name) => {
      try {
        const { data } = await axios.put(viewUrl(view), { name });
        await refreshViews();
        if (activeViewRef.current?._id === view._id) {
          activeViewRef.current = data;
          setActiveView(data);
        }
        return true;
      } catch (err) {
        viewsSnackError(err, 'Failed to rename the view');
        return false;
      }
    },
    [viewUrl, refreshViews, viewsSnackError]
  );

  const handleShareView = React.useCallback(
    async (view) => {
      try {
        const { data } = await axios.put(viewUrl(view), { scope: 'group' });
        await refreshViews();
        if (activeViewRef.current?._id === view._id) {
          activeViewRef.current = data;
          setActiveView(data);
        }
      } catch (err) {
        viewsSnackError(err, 'Failed to share the view');
      }
    },
    [viewUrl, refreshViews, viewsSnackError]
  );

  const handleDeleteView = React.useCallback(
    async (view) => {
      try {
        await axios.delete(viewUrl(view));
        if (activeViewRef.current?._id === view._id) handleApplyStandard();
        await refreshViews();
      } catch (err) {
        viewsSnackError(err, 'Failed to delete the view');
      }
    },
    [viewUrl, refreshViews, handleApplyStandard, viewsSnackError]
  );

  // Hide-gesture routing while a view is active (plan §3.5): the context menu's
  // Hide edits the view OVERLAY, not the person's shared `hidden` flag. The
  // hide-connector guard analyses the EFFECTIVE visibility (overlay counts as
  // hidden), and a confirmed multi-hide lands in the overlay as one update.
  const viewHide = React.useMemo(() => {
    if (!activeView) return null;
    const overlay = viewHiddenOverlay || new Set();
    return {
      members: memberRecords.map((m) =>
        overlay.has(uidOf(m)) ? { ...m, hidden: true } : m
      ),
      hide: (pids) => {
        const pidSet = new Set(pids);
        const next = new Set(overlay);
        memberRecords.forEach((m) => {
          if (pidSet.has(m.pid)) next.add(uidOf(m));
        });
        updateViewHiddenOverlay(next);
      }
    };
  }, [
    activeView,
    viewHiddenOverlay,
    memberRecords,
    uidOf,
    updateViewHiddenOverlay
  ]);

  // Unhide (Hidden Members panel). While a view is active the panel lists ONLY
  // overlay-hidden people (see hiddenMembers above) and unhide edits the
  // overlay exclusively — a view context must never write shared family
  // records ("Unhide All" on a view was the review's mass-unhide hazard).
  const unhideByPids = (pids) => {
    const pidSet = new Set(pids);
    if (activeViewRef.current && viewHiddenOverlay) {
      const nextOverlay = new Set(viewHiddenOverlay);
      let overlayChanged = false;
      memberRecords.forEach((m) => {
        if (!pidSet.has(m.pid)) return;
        if (nextOverlay.has(uidOf(m))) {
          nextOverlay.delete(uidOf(m));
          overlayChanged = true;
        }
      });
      if (overlayChanged) updateViewHiddenOverlay(nextOverlay);
      setSelectedHiddenPids([]);
      return;
    }
    setMemberRecords((prev) =>
      prev.map((m) => (pidSet.has(m.pid) ? { ...m, hidden: false } : m))
    );
    setPedigreeState((prev) => ({
      ...prev,
      save: PEDIGREE.saveStatus.IN_PROGRESS
    }));
    setSelectedHiddenPids([]);
  };

  const unhideSelected = () => unhideByPids(selectedHiddenPids);
  const unhideAll = () => unhideByPids(hiddenMembers.map((m) => m.pid));

  const onSumbit = React.useCallback(() => {
    if (isLocked) return Promise.resolve();
    if (pedigreeState.save === PEDIGREE.saveStatus.SAVED) {
      return Promise.resolve();
    }
    const latestRecords = memberRecordsRef.current;
    // Return the request promise so the scheduler below can SERIALISE saves —
    // never overlapping two in-flight requests (which would re-mint UPNs for
    // not-yet-acknowledged nodes and skip numbers).
    let request = Promise.resolve();
    if (latestRecords.length > 0) {
      request = axios
        .post(APICONSTANTS.pedigreeConfig.replace(':user_id', user._id), {
          config: latestRecords,
          familyId: user.familyId
        })
        .then(({ status, data }) => {
          if (status === 201) {
            setDateModifed(data.dateModified);

            queryClient.setQueryData(['probandFamilies'], (prev = []) => {
              const records = Array.isArray(latestRecords) ? latestRecords : [];
              const activeProband = records.find((m) => m?.isProband === true);
              const nextFirstName = activeProband?.firstName || '';
              const nextLastName = activeProband?.lastName || '';

              return prev.map((row) => {
                if (String(row?._id) !== String(user?._id)) return row;
                if (!activeProband) return row;
                return {
                  ...row,
                  firstName: nextFirstName,
                  lastName: nextLastName
                };
              });
            });

            // Family member set may have changed (added/removed) — mark the
            // family-records table caches stale so it shows fresh rows on return.
            queryClient.invalidateQueries({ queryKey: ['probandBrowse'] });
            queryClient.invalidateQueries({ queryKey: ['familySearchIndex'] });
            queryClient.invalidateQueries({ queryKey: ['probandFamilies'] });

            // Signal that we need to update UPNs
            if (data.familyData && Array.isArray(data.familyData)) {
              // Apply the assigned upn/familyMemberId to the live ref
              // SYNCHRONOUSLY (matched by pid). A serialised follow-up save can
              // fire before React flushes the reconciliation below; without this
              // it would re-send these nodes as un-minted and the backend would
              // re-mint them, skipping UPNs. setFamilyData still drives the UI.
              // IDENTITY-ONLY merge — see saveReconciliation.js: spreading the
              // whole echoed row reverted members edited mid-save (rapid-add
              // wrong-generation bug).
              const fd = data.familyData;
              memberRecordsRef.current = applyIdentityReconciliation(
                memberRecordsRef.current,
                fd
              );
              hasPendingUpnUpdates.current = true;
              setFamilyData(fd);
            }

            // Here update diagnoses
            const diagnoses = getUniqueDiagnoses(latestRecords);
            dispatchUpdate({
              type: EVENTS.SET_PEDIGREE_SELECTED_USER,
              value: {
                ...users,
                diagnoses: diagnoses
              }
            });
          }
        })
        .catch((err) => {
          if (err.status === 413) {
            setSnack({
              status: true,
              msg: 'Pedigree Maximum members has exceeded',
              severity: 'warning'
            });
          } else {
            setSnack({
              status: true,
              msg: 'An error occured',
              severity: 'error'
            });
          }
        });
    }

    // Flip to SAVED only once the round trip has SETTLED and the identity
    // reconciliation above has been applied — not at POST-fire time. This keeps
    // the "Saving…" indicator honest for the whole in-flight window and lets it
    // drive the Add Relative disabled state (the true race window is fire →
    // response-merge, seconds on a large QE-encrypted family — the old
    // fire-time flip re-enabled the buttons during the dangerous part).
    return request.finally(() => {
      setPedigreeState((prev) => ({
        ...prev,
        save: PEDIGREE.saveStatus.SAVED
      }));
    });
  }, [pedigreeState, queryClient]);

  // Stable debounced + serialised save.
  //
  // The previous `debounce(onSumbit, 0)` was RECREATED on every render, so each
  // instance had its own `timeout` and `clearTimeout` never cancelled a prior
  // pending call. With wait=0, every memberRecords change fired its own near-
  // immediate save, so rapidly adding members produced a burst of OVERLAPPING
  // POST /pedigree-config requests. Each re-sent nodes whose server-assigned
  // familyMemberId hadn't come back yet, and the backend minted a FRESH UPN for
  // each — so the same node was minted several times and the in-between numbers
  // were burned (the skipped UPNs: 9, 10, 11, _12_, 13, …).
  //
  // This single stable controller (a) COALESCES rapid edits into one save and
  // (b) never overlaps an in-flight save — edits that land mid-save trigger one
  // re-run after it resolves, by which point the saved nodes carry their
  // familyMemberId and are no longer re-minted. No cleanup-on-unmount: like the
  // old code, a trailing save still flushes if the user navigates away.
  const onSumbitRef = React.useRef(onSumbit);
  onSumbitRef.current = onSumbit;
  const saveControlRef = React.useRef(null);
  if (!saveControlRef.current) {
    let timer = null;
    let inFlight = false;
    let pending = false;
    const fire = () => {
      if (inFlight) {
        pending = true;
        return;
      }
      inFlight = true;
      Promise.resolve(onSumbitRef.current())
        .catch(() => {})
        .finally(() => {
          inFlight = false;
          if (pending) {
            pending = false;
            fire();
          }
        });
    };
    saveControlRef.current = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(fire, 300);
    };
  }

  React.useEffect(() => {
    saveControlRef.current();
  }, [memberRecords]); // eslint-disable-line react-hooks/exhaustive-deps

  /*  */

  // Save indicator: "Saving…" while edits are committing, then a brief "Saved"
  // confirmation that fades away. The chip used to be hidden entirely, so a
  // rapid add burst gave no feedback at all and read as the app hanging.
  const [savedFlash, setSavedFlash] = React.useState(false);
  const prevSaveStateRef = useRef(pedigreeState.save);
  React.useEffect(() => {
    const was = prevSaveStateRef.current;
    prevSaveStateRef.current = pedigreeState.save;
    if (
      was === PEDIGREE.saveStatus.IN_PROGRESS &&
      pedigreeState.save === PEDIGREE.saveStatus.SAVED
    ) {
      setSavedFlash(true);
      const timer = setTimeout(() => setSavedFlash(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [pedigreeState.save]);

  // Borderless icon + text to the right of the "You are editing" chip,
  // formatted like the FHQ family-details indicator (questionnaire/
  // SavedIndicator.jsx): blue save icon while saving, green tick once saved —
  // momentary information that flashes up, not a persistent control.
  const save_info = React.useMemo(() => {
    const saving = pedigreeState.save === PEDIGREE.saveStatus.IN_PROGRESS;
    if (!saving && !savedFlash) return null;
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          color: saving ? 'info.main' : 'success.main'
        }}
      >
        {saving ? (
          <SaveOutlined sx={{ fontSize: 15 }} />
        ) : (
          <CheckCircleOutlined sx={{ fontSize: 15 }} />
        )}
        <Typography
          variant="caption"
          sx={{ fontSize: '0.72rem', fontWeight: 700 }}
        >
          {saving ? 'Saving in progress' : 'Saved just now'}
        </Typography>
      </Box>
    );
  }, [pedigreeState.save, savedFlash]);

  return (
    <Box>
      {/* Hidden export elements — captured by ExportMenu via html2canvas(getElementById(...)),
          which renders the element by reference regardless of where it sits. Positioned
          OFF-SCREEN (not merely z-index:-1) so they are never revealed on the canvas: while
          the worker computes the layout the SVG isn't painted yet, and a z-index:-1 element
          with nothing covering it would flash at the top-left until the chart appears. */}
      <div
        id="pedigree-export-logo"
        style={{
          position: 'absolute',
          top: 0,
          left: -100000,
          backgroundColor: 'white',
          padding: 12
        }}
      >
        <img
          src={theme.logoURL && theme.logoName ? theme.logoURL : trakgeneLogo}
          alt="Logo"
          style={{ height: 50 }}
          crossOrigin="anonymous"
        />
      </div>
      <div
        id="diagnoses-legend"
        style={{
          position: 'absolute',
          top: 0,
          left: -100000,
          width: 320,
          backgroundColor: 'white',
          padding: 12
        }}
      >
        {labelsSet.showRecordStatus &&
          (hasPrimaryRecord || hasSecondaryRecord) && (
            <div
              style={{
                display: 'flex',
                gap: 15,
                flexWrap: 'wrap',
                marginBottom: 8
              }}
            >
              {hasPrimaryRecord && (
                <RecordBadgeSwatch
                  role="primary"
                  teal={theme.accent || '#2ba9bf'}
                  label="Primary record"
                />
              )}
              {hasSecondaryRecord && (
                <RecordBadgeSwatch
                  role="secondary"
                  teal={theme.accent || '#2ba9bf'}
                  label="Secondary record (read-only)"
                />
              )}
            </div>
          )}
        {labelsSet.diseases && users.diagnoses.length > 0 && (
          <div style={{ marginBottom: 10 }}>
            <Typography style={{ fontSize: '1rem', fontWeight: 'bold' }}>
              {t('Diseases')}
            </Typography>
            <LegendMenu
              dataset={memberRecords}
              diagnoses={users.diagnoses}
              id="diagnoses-legend-export"
              showLegendTxt
              setDataset={setMemberRecords}
              draggable={false}
              labelsSet={labelsSet}
              carrierPresentStates={displayCarrier.presentStatesByDisease}
              showCarrierMarks={labelsSet?.showCarrierMarks !== false}
              carrierStyle={carrierStyle.activeStyle}
              showRecordLegend={false}
            />
          </div>
        )}
        {users.phenotypes.length > 0 && labelsSet.phenotypes !== false && (
          <div style={{ marginBottom: 10 }}>
            <Typography style={{ fontSize: '1rem', fontWeight: 'bold' }}>
              {t('Phenotypes')}
            </Typography>
            <PhenotypesLegend
              phenotypes={users.phenotypes}
              id="phenotypes-legend-export"
              dataset={memberRecords}
              setDataset={setMemberRecords}
              labelsSet={labelsSet}
              draggable={false}
            />
          </div>
        )}
        {users.genotypes.length > 0 && labelsSet.genotypes !== false && (
          <div style={{ marginBottom: 10 }}>
            <Typography style={{ fontSize: '1rem', fontWeight: 'bold' }}>
              {t('Genotypes')}
            </Typography>
            <GenotypesLegend
              genotypes={users.genotypes}
              id="genotypes-legend-export"
              dataset={memberRecords}
              setDataset={setMemberRecords}
              labelsSet={labelsSet}
              draggable={false}
            />
          </div>
        )}
        <VariantPathogenicityLegend members={memberRecords} />
      </div>
      {/* Legacy inline save text, just RIGHT of the "You are editing" chip.
          Superseded by the stacked PedigreeSaveIndicator (rendered inside
          ChartBox) when ENABLE_STACKED_SAVE_INDICATOR is on. */}
      {!ENABLE_STACKED_SAVE_INDICATOR && (
        <Box
          sx={{
            position: 'absolute',
            top: 11,
            left: 'calc(50% + 84px)',
            zIndex: 1300
          }}
        >
          {save_info}
        </Box>
      )}
      <RightContainer
        ref={familyCardRef}
        style={{
          pointerEvents: 'none',
          flexDirection: 'column',
          ...(preview ? { display: 'none' } : {}),
          ...(sidebarState.expand
            ? { left: 12, right: 'auto' }
            : {
                right: sidebarState.open ? 412 : 28,
                borderRadius: 8,
                borderRight: '1px solid #e2e8f0',
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)'
              })
        }}
      >
        {/* Heading */}
        <Box
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            margin: '-12px -16px 6px',
            padding: '10px 16px',
            backgroundColor: '#f5f6f8',
            borderRadius: '8px 8px 0 0'
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1, pointerEvents: 'none' }}>
            <Typography
              title={headlineId}
              sx={{
                color: theme.header,
                fontWeight: 700,
                fontSize: '1.2rem',
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {headlineId}
            </Typography>

            {useExternalPrimary && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 0.5,
                  mt: 0.5,
                  pointerEvents: 'auto'
                }}
              >
                <Chip
                  label={headerTitle}
                  size="small"
                  title={headerTitle}
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    backgroundColor: 'rgba(43, 169, 191, 0.15)',
                    color: '#1a7a8a',
                    maxWidth: 160,
                    '& .MuiChip-label': { px: 0.75 }
                  }}
                />
                <Typography
                  sx={{ fontSize: '0.7rem', color: 'text.secondary' }}
                >
                  Master ID
                </Typography>
                {secondaryIds.map((id) => (
                  <Chip
                    key={id}
                    label={id}
                    size="small"
                    title={id}
                    sx={{
                      height: 20,
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      backgroundColor: 'rgba(0,0,0,0.06)',
                      color: 'text.secondary',
                      maxWidth: 140,
                      '& .MuiChip-label': { px: 0.75 }
                    }}
                  />
                ))}
              </Box>
            )}

            {!anonymousActive &&
              !useExternalPrimary &&
              externalFamilyIds.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    mt: 0.5,
                    pointerEvents: 'auto'
                  }}
                >
                  <Typography
                    sx={{ fontSize: '0.7rem', color: 'text.secondary' }}
                  >
                    Also known as
                  </Typography>
                  {externalFamilyIds.map((id, idx) => (
                    <Chip
                      key={id}
                      label={id}
                      size="small"
                      title={id}
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        fontWeight: idx === 0 ? 600 : 500,
                        backgroundColor:
                          idx === 0
                            ? 'rgba(43, 169, 191, 0.15)'
                            : 'rgba(0,0,0,0.06)',
                        color: idx === 0 ? '#1a7a8a' : 'text.secondary',
                        maxWidth: 160,
                        '& .MuiChip-label': { px: 0.75 }
                      }}
                    />
                  ))}
                </Box>
              )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              pointerEvents: 'auto',
              flexShrink: 0
            }}
          >
            {viewsEnabled && (
              <ViewsMenu
                views={views}
                activeView={activeView}
                userType={loggedInUser?.userType}
                isLocked={isLocked}
                accent={theme.accent}
                onApplyView={handleApplyView}
                onApplyStandard={handleApplyStandard}
                onSaveAs={handleSaveAsView}
                onRename={handleRenameView}
                onShare={handleShareView}
                onDelete={handleDeleteView}
                onUpdateLayout={handleUpdateViewLayout}
              />
            )}
            <Tooltip title="Family record">
              <IconButton
                size="small"
                onClick={() => chartBoxRef.current?.openFamilyRecord()}
              >
                <PeopleAlt fontSize="small" sx={{ color: theme.accent }} />
              </IconButton>
            </Tooltip>
            {!isLocked && (
              <Tooltip title="Bulk edit family members">
                <IconButton
                  size="small"
                  data-va-anchor="pedigree.header.bulkEdit"
                  onClick={() => chartBoxRef.current?.openBulkEdit()}
                >
                  <PlaylistAdd fontSize="small" sx={{ color: theme.accent }} />
                </IconButton>
              </Tooltip>
            )}
            {/* Read-only, so deliberately NOT gated on isLocked — the history
                is most needed when the pedigree is locked after a bad edit. */}
            {user?.familyId && (
              <Tooltip title="Pedigree history">
                <IconButton size="small" onClick={() => setHistoryOpen(true)}>
                  <History fontSize="small" sx={{ color: theme.accent }} />
                </IconButton>
              </Tooltip>
            )}
            {analysis?.family && (
              <Tooltip
                title={
                  analysisOverlayOn
                    ? 'Hide pedigree analysis overlay'
                    : 'Show pedigree analysis overlay'
                }
              >
                <IconButton size="small" onClick={toggleAnalysisOverlay}>
                  <Insights
                    fontSize="small"
                    sx={{ color: analysisOverlayOn ? theme.accent : '#bdbdbd' }}
                  />
                </IconButton>
              </Tooltip>
            )}
            {!isLocked && analysis?.family && (
              <CascadeScreeningButton
                analysis={analysis}
                diagnoses={users.diagnoses}
                labelsSet={labelsSet}
                members={standardVisibleMembers}
                accent={theme.accent}
                onApply={(pids) => chartBoxRef.current?.applySelection(pids)}
              />
            )}
            <Tooltip
              title={legendCollapsed ? 'Expand panel' : 'Collapse panel'}
            >
              <IconButton
                size="small"
                onClick={() => setLegendCollapsed((prev) => !prev)}
              >
                {legendCollapsed ? (
                  <UnfoldMore fontSize="small" sx={{ color: theme.accent }} />
                ) : (
                  <UnfoldLess fontSize="small" sx={{ color: theme.accent }} />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Legend Menu + Dates */}
        <Collapse in={!legendCollapsed}>
          <div
            style={{
              pointerEvents: 'auto',
              overflowY: 'auto',
              overflowX: 'hidden',
              maxHeight: 'calc(100vh - 230px)',
              minHeight: 0
            }}
          >
            {labelsSet.showRecordStatus &&
              (hasPrimaryRecord || hasSecondaryRecord) && (
                <div
                  style={{
                    display: 'flex',
                    gap: 15,
                    flexWrap: 'wrap',
                    marginBottom: 8
                  }}
                >
                  {hasPrimaryRecord && (
                    <RecordBadgeSwatch
                      role="primary"
                      teal={theme.accent || '#2ba9bf'}
                      label="Primary record"
                    />
                  )}
                  {hasSecondaryRecord && (
                    <RecordBadgeSwatch
                      role="secondary"
                      teal={theme.accent || '#2ba9bf'}
                      label="Secondary record (read-only)"
                    />
                  )}
                </div>
              )}
            {labelsSet.diseases && (
              <div
                style={{
                  marginBottom: users.diagnoses.length ? '15px' : '0px'
                }}
              >
                {users.diagnoses.length > 0 ? (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Typography
                      style={{
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        flexGrow: 1
                      }}
                    >
                      {t('Diseases')}
                    </Typography>
                    {someDiseasesHidden && (
                      <Button
                        size="small"
                        variant="text"
                        onClick={showAllDiseases}
                        sx={{
                          fontSize: '0.7rem',
                          py: 0,
                          px: 0.5,
                          minWidth: 'auto'
                        }}
                      >
                        {t('Show all')}
                      </Button>
                    )}
                  </div>
                ) : null}

                <LegendMenu
                  dataset={memberRecords}
                  diagnoses={users.diagnoses}
                  id="diagnoses-legend-canvas"
                  showLegendTxt
                  setDataset={setMemberRecords}
                  draggable={true}
                  labelsSet={labelsSet}
                  carrierPresentStates={displayCarrier.presentStatesByDisease}
                  showCarrierMarks={labelsSet?.showCarrierMarks !== false}
                  carrierStyle={carrierStyle.activeStyle}
                  showRecordLegend={false}
                />
              </div>
            )}

            {/* Phenotypes menu */}
            {users.phenotypes.length > 0 && labelsSet.phenotypes !== false && (
              <div style={{ marginBottom: '15px' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Typography
                    style={{
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      flexGrow: 1
                    }}
                  >
                    {t('Phenotypes')}
                  </Typography>
                  {somePhenotypesHidden && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={showAllPhenotypes}
                      sx={{
                        fontSize: '0.7rem',
                        py: 0,
                        px: 0.5,
                        minWidth: 'auto'
                      }}
                    >
                      {t('Show all')}
                    </Button>
                  )}
                </div>
                <PhenotypesLegend
                  draggable={true}
                  phenotypes={users.phenotypes}
                  dataset={memberRecords}
                  setDataset={setMemberRecords}
                  id="phenotypes-legend-canvas"
                  labelsSet={labelsSet}
                />
              </div>
            )}

            {/* Genotypes menu */}
            {users.genotypes.length > 0 && labelsSet.genotypes !== false && (
              <div style={{ marginBottom: '15px' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Typography
                    style={{
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      flexGrow: 1
                    }}
                  >
                    {t('Genotypes')}
                  </Typography>
                  {someGenotypesHidden && (
                    <Button
                      size="small"
                      variant="text"
                      onClick={showAllGenotypes}
                      sx={{
                        fontSize: '0.7rem',
                        py: 0,
                        px: 0.5,
                        minWidth: 'auto'
                      }}
                    >
                      {t('Show all')}
                    </Button>
                  )}
                </div>
                <GenotypesLegend
                  draggable={true}
                  genotypes={users.genotypes}
                  dataset={memberRecords}
                  setDataset={setMemberRecords}
                  id="genotypes-legend-canvas"
                  labelsSet={labelsSet}
                />
              </div>
            )}

            <VariantPathogenicityLegend members={memberRecords} />
          </div>

          {/* Pedigree Dates */}
          <Box
            id="pedigree-dates"
            sx={{
              flexShrink: 0,
              mt: 1,
              pt: 1,
              padding: '8px',
              borderTop: '1px solid #e2e8f0',
              pointerEvents: 'none'
            }}
          >
            <PedigreeDates
              dateModified={dateModified}
              theme={theme}
              showPrintDate={false}
            />
          </Box>
        </Collapse>

        <UnrecognisedCarrierStatusNotice members={memberRecords} />

        {hiddenMembers.length > 0 && (
          <Box
            sx={{
              mt: 1,
              pointerEvents: 'auto',
              borderTop: '1px solid #e2e8f0',
              pt: 1
            }}
          >
            {/* Header row — toggle + count */}
            <Box
              onClick={() => setHiddenPanelOpen((prev) => !prev)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer'
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                Hidden Members ({hiddenMembers.length})
              </Typography>
              {hiddenPanelOpen ? (
                <ExpandLess fontSize="small" sx={{ opacity: 0.6 }} />
              ) : (
                <ExpandMore fontSize="small" sx={{ opacity: 0.6 }} />
              )}
            </Box>

            <Collapse in={hiddenPanelOpen}>
              {/* Action row */}
              <Box
                sx={{
                  display: 'flex',
                  gap: 0.5,
                  mt: 0.75,
                  mb: 0.5
                }}
              >
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', py: 0.25, flex: 1 }}
                  onClick={unhideAll}
                >
                  Unhide All
                </Button>
                {selectedHiddenPids.length > 0 && (
                  <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    sx={{ fontSize: '0.7rem', py: 0.25, flex: 1 }}
                    onClick={unhideSelected}
                  >
                    Unhide ({selectedHiddenPids.length})
                  </Button>
                )}
              </Box>

              {/* Member list */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  maxHeight: 160,
                  overflowY: 'auto'
                }}
              >
                {hiddenMembers.map((m) => {
                  const isSelected = selectedHiddenPids.includes(m.pid);
                  return (
                    <Box
                      key={m.pid}
                      onClick={() => toggleHiddenSelection(m.pid)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        cursor: 'pointer',
                        borderRadius: 1,
                        px: 0.5,
                        bgcolor: isSelected ? 'action.selected' : 'transparent',
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={isSelected}
                        onChange={() => toggleHiddenSelection(m.pid)}
                        onClick={(e) => e.stopPropagation()}
                        sx={{ p: 0.5 }}
                      />
                      <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                        {[m.firstName, m.lastName].filter(Boolean).join(' ') ||
                          m.upn ||
                          m.pid}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Collapse>
          </Box>
        )}
      </RightContainer>

      {!preview && (
        <Box
          sx={{ position: 'fixed', zIndex: 9, width: 350 }}
          style={{
            top: familyCardBottom != null ? familyCardBottom + 8 : 0,
            // Only ever set 'hidden', never 'visible'. `visibility` is the one
            // property a descendant can use to un-hide itself against an
            // ancestor, and the multi-tab workspace hides INACTIVE tabs with
            // visibility:hidden (deliberately, so the canvas keeps its layout
            // box). An explicit 'visible' here made every background tab's
            // banner float over the active chart at the same fixed coordinates,
            // with a live Review button opening the wrong family's FHQ.
            ...(familyCardBottom == null ? { visibility: 'hidden' } : {}),
            ...(sidebarState.expand
              ? { left: 12, right: 'auto' }
              : { right: sidebarState.open ? 412 : 28 })
          }}
        >
          <FhqReviewBanner
            probandId={user?._id}
            patient={patient}
            enabled={
              content?.settings?.systemFeatures
                ?.FEATURE_FAMILY_HISTORY_QUESTIONNAIRE !== false
            }
            onReview={() => chartBoxRef.current?.openFamilyHistory()}
          />
        </Box>
      )}

      {historyOpen && (
        <CustomDialog
          title="Pedigree History"
          open={historyOpen}
          handleClose={() => setHistoryOpen(false)}
          minHeight="auto"
          maxWidth="lg"
          fullWidth
        >
          <ModificationHistoryModal
            modificationType={CONSTANTS.auditLogType.pedigreeChart}
            familyId={user?.familyId}
          />
        </CustomDialog>
      )}
      <ChartBox
        ref={chartBoxRef}
        pedRef={pedRef}
        user={user}
        family={family}
        options={options}
        setPedigreeState={setPedigreeState}
        isSaving={pedigreeState.save === PEDIGREE.saveStatus.IN_PROGRESS}
        colors={colors}
        memberRecords={memberRecords}
        canvasMembers={canvasMembers}
        carrier={displayCarrier}
        carrierStyle={carrierStyle}
        analysis={analysis}
        setMemberRecords={setMemberRecords}
        labelsSet={labelsSet}
        setLabelsSet={setLabelsSet}
        customPositions={customPositions}
        setCustomPositions={setCustomPositions}
        establishedOrder={establishedOrder}
        orderLocked={orderLocked}
        setOrderLocked={setOrderLocked}
        setEstablishedOrder={setEstablishedOrder}
        setOptions={setOptions}
        isLocked={isLocked}
        isMine={isMine}
        lockState={lockState}
        requestStatus={requestStatus}
        onRequestLock={onRequestLock}
        onCancelRequest={onCancelRequest}
        onForceAcquireLock={onForceAcquireLock}
        pendingRequest={pendingRequest}
        onRespondToRequest={onRespondToRequest}
        personLockHook={personLockHook}
        isMigrated={isMigrated}
        onSidebarChange={setSidebarState}
        linkStatusMap={linkStatusMap}
        refetchLinkStatus={refetchLinkStatus}
        onHistoryCheckpoint={
          layoutConfig.ENABLE_UNDO_REDO ? historyCheckpoint : undefined
        }
        onAutomaticLayoutWrite={noteAutomaticLayoutWrite}
        onHistoryUndo={handleHistoryUndo}
        onHistoryRedo={handleHistoryRedo}
        canUndo={undoRedoActive && historyCanUndo}
        canRedo={undoRedoActive && historyCanRedo}
        viewHide={viewHide}
      />
    </Box>
  );
}

export default Pedigree;
